/**
 * SEO.js — Per-page SEO + GEO (Generative Engine Optimization) component
 *
 * Usage:
 *   <SEO
 *     title="Generate Content Ideas | Socialixer"
 *     description="AI-powered content idea generator for Instagram, Twitter, LinkedIn."
 *     path="/app/ideas"
 *   />
 *
 * For blog articles:
 *   <SEO
 *     title="How to Grow Instagram Followers | Socialixer Blog"
 *     description="Step-by-step guide to growing your Instagram following with AI."
 *     path="/blog/grow-instagram-followers"
 *     type="Article"
 *     article={{ author: "Socialixer Team", publishedTime: "2026-03-26", modifiedTime: "2026-03-26" }}
 *   />
 *
 * GEO signals this component injects:
 *  - Per-page <title> → unique, descriptive, keyword-rich
 *  - Per-page meta description → concise, entity-rich
 *  - Canonical URL → prevents duplicate content penalty
 *  - og:title / og:description / og:type → social sharing previews
 *  - Twitter Card tags → Twitter/X sharing
 *  - JSON-LD BreadcrumbList → AI engines understand site hierarchy
 *  - JSON-LD Article schema → blog posts discoverable by AI engines
 */

import { useEffect } from 'react';

const BASE_URL = 'https://socialixer.com';

const DEFAULT_META = {
    title: 'Socialixer — AI Social Media Automation Platform',
    description:
        'Socialixer is an AI-powered social media automation platform. Schedule posts, discover content gaps, generate ideas, track follower growth, and manage leads across Instagram, Twitter, LinkedIn, YouTube, Facebook, Reddit, and TikTok.',
    image: `${BASE_URL}/og-image.png`,
};

function updateTag(selector, attr, value) {
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement('meta');
        const [, name, nameVal] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (name) el.setAttribute(name, nameVal);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
}

function removeTag(selector) {
    const el = document.querySelector(selector);
    if (el) el.remove();
}

function updateLd(id, data) {
    let script = document.getElementById(id);
    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data, null, 2);
}

function removeLd(id) {
    const el = document.getElementById(id);
    if (el) el.remove();
}

export default function SEO({
    title,
    description,
    path = '/',
    image,
    type = 'WebPage',
    breadcrumbs = null,
    noIndex = false,
    article = null, // { author, publishedTime, modifiedTime, tags }
}) {
    const resolvedTitle = title || DEFAULT_META.title;
    const resolvedDesc = description || DEFAULT_META.description;
    const resolvedImage = image || DEFAULT_META.image;
    const canonicalUrl = `${BASE_URL}${path}`;

    useEffect(() => {
        // ── Title ────────────────────────────────────────────────────────────────
        document.title = resolvedTitle;

        // ── Meta description ─────────────────────────────────────────────────────
        updateTag('meta[name="description"]', 'content', resolvedDesc);

        // ── Robots ───────────────────────────────────────────────────────────────
        updateTag('meta[name="robots"]', 'content', noIndex ? 'noindex, nofollow' : 'index, follow');

        // ── Canonical ────────────────────────────────────────────────────────────
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = canonicalUrl;

        // ── Open Graph ───────────────────────────────────────────────────────────
        const ogType = type === 'Article' ? 'article' : 'website';
        updateTag('meta[property="og:title"]', 'content', resolvedTitle);
        updateTag('meta[property="og:description"]', 'content', resolvedDesc);
        updateTag('meta[property="og:image"]', 'content', resolvedImage);
        updateTag('meta[property="og:url"]', 'content', canonicalUrl);
        updateTag('meta[property="og:type"]', 'content', ogType);
        updateTag('meta[property="og:site_name"]', 'content', 'Socialixer');

        // ── Article-specific OG tags ─────────────────────────────────────────────
        if (type === 'Article' && article) {
            if (article.publishedTime) {
                updateTag('meta[property="article:published_time"]', 'content', article.publishedTime);
            }
            if (article.modifiedTime) {
                updateTag('meta[property="article:modified_time"]', 'content', article.modifiedTime);
            }
            if (article.author) {
                updateTag('meta[property="article:author"]', 'content', article.author);
            }
            if (article.tags) {
                article.tags.forEach((tag, i) => {
                    updateTag(`meta[property="article:tag"][data-idx="${i}"]`, 'content', tag);
                });
            }
        } else {
            // Clean up article tags when navigating away from article pages
            removeTag('meta[property="article:published_time"]');
            removeTag('meta[property="article:modified_time"]');
            removeTag('meta[property="article:author"]');
            document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());
        }

        // ── Twitter Card ─────────────────────────────────────────────────────────
        updateTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
        updateTag('meta[name="twitter:site"]', 'content', '@socialixerapp');
        updateTag('meta[name="twitter:title"]', 'content', resolvedTitle);
        updateTag('meta[name="twitter:description"]', 'content', resolvedDesc);
        updateTag('meta[name="twitter:image"]', 'content', resolvedImage);

        // ── JSON-LD Breadcrumb ───────────────────────────────────────────────────
        if (breadcrumbs && breadcrumbs.length > 0) {
            const ldBreadcrumb = {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                'itemListElement': [
                    { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
                    ...breadcrumbs.map((bc, idx) => ({
                        '@type': 'ListItem',
                        position: idx + 2,
                        name: bc.name,
                        item: `${BASE_URL}${bc.path}`,
                    })),
                ],
            };
            updateLd('ld-breadcrumb', ldBreadcrumb);
        } else {
            removeLd('ld-breadcrumb');
        }

        // ── JSON-LD Article (for blog posts — critical for GEO) ──────────────────
        if (type === 'Article' && article) {
            const ldArticle = {
                '@context': 'https://schema.org',
                '@type': 'Article',
                'headline': resolvedTitle,
                'description': resolvedDesc,
                'image': resolvedImage,
                'url': canonicalUrl,
                'datePublished': article.publishedTime,
                'dateModified': article.modifiedTime || article.publishedTime,
                'author': {
                    '@type': article.authorType || 'Organization',
                    'name': article.author || 'Socialixer',
                    'url': BASE_URL,
                },
                'publisher': {
                    '@type': 'Organization',
                    'name': 'Socialixer',
                    'url': BASE_URL,
                    'logo': {
                        '@type': 'ImageObject',
                        'url': `${BASE_URL}/logo192.png`,
                    },
                },
                'mainEntityOfPage': {
                    '@type': 'WebPage',
                    '@id': canonicalUrl,
                },
            };
            if (article.tags && article.tags.length > 0) {
                ldArticle.keywords = article.tags.join(', ');
            }
            updateLd('ld-article', ldArticle);
        } else {
            removeLd('ld-article');
        }

        // ── Cleanup on unmount ───────────────────────────────────────────────────
        return () => {
            removeLd('ld-breadcrumb');
            removeLd('ld-article');
        };
    }, [resolvedTitle, resolvedDesc, resolvedImage, canonicalUrl, noIndex, breadcrumbs, type, article]);

    return null; // renders nothing — only side-effects
}
